import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import * as BookmarkActions from '../actions/bookmark.actions';
import { BookmarkService } from '../services/bookmark.service';
import { dispatchAppError } from '../actions/core.actions';
import { FirestoreError } from '../errors/firestore-error';
import { FirebaseCallableFunctionError } from '../errors/firebase-callable-function-error';
import { AppError } from '../errors/app-error';

@Injectable()
export class BookmarkEffects {
  private LOG_SOURCE = this.constructor.name;

  addBookmark$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.addBookmark),
      switchMap(action =>
        this.bookmarkService.addBookmark(action.userId, action.subjectId, action.problemId).pipe(
          map(response => BookmarkActions.addBookmarkSuccess({ response })),
          catchError(e => of(BookmarkActions.addBookmarkFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );
  deleteBookmark$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.deleteBookmark),
      switchMap(action =>
        this.bookmarkService.deleteBookmark(action.userId, action.subjectId, action.problemId).pipe(
          map(response => BookmarkActions.deleteBookmarkSuccess({ response })),
          catchError(e => of(BookmarkActions.deleteBookmarkFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );
  findBookmark$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.findBookmark),
      switchMap(action =>
        this.bookmarkService.findBookmark(action.userId).pipe(
          map(bookmark => BookmarkActions.findBookmarkSuccess({ bookmark })),
          catchError(e => {
            const error = e instanceof AppError ? e : FirestoreError.from(e);
            return of(BookmarkActions.findBookmarkFailure(), dispatchAppError({ source: this.LOG_SOURCE, error }));
          })
        )
      )
    )
  );
  findBookmarkCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.findBookmarkCount),
      switchMap(action =>
        this.bookmarkService.findBookmarkCount(action.userId, action.subjectId).pipe(
          map(response => BookmarkActions.findBookmarkCountSuccess({ response })),
          catchError(e =>
            of(
              BookmarkActions.findBookmarkCountFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );
  findBookmarkProblems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.findBookmarkProblems),
      switchMap(action =>
        this.bookmarkService.findBookmarkProblems(action.condition).pipe(
          map(problems => BookmarkActions.findBookmarkProblemsSuccess({ subjectId: action.condition.subjectId, problems })),
          catchError(e =>
            of(
              BookmarkActions.findBookmarkProblemsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );
  findBookmarkProblemIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkActions.findBookmarkProblemIds),
      switchMap(action =>
        this.bookmarkService.findBookmarkProblemIds(action.condition).pipe(
          map(bookmarkProblemIds => BookmarkActions.findBookmarkProblemIdsSuccess({ bookmarkProblemIds })),
          catchError(e =>
            of(
              BookmarkActions.findBookmarkProblemIdsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );

  constructor(private actions$: Actions, private bookmarkService: BookmarkService) {}
}
